import { AssessmentMatrixPage } from "@components/campaigns/AssessmentMatrixPage";
import type {
  CoverageCell,
  CoverageDimension,
  CoverageStep,
} from "@components/campaigns/CoverageMatrix";
import { SelectionProcessEditor } from "@components/campaigns/SelectionProcessEditor";
import type { DimensionOption } from "@components/campaigns/StepAssessmentPlanFields";
import { BriefStage } from "@components/campaigns/stages/BriefStage";
import { MarketMappingStage } from "@components/campaigns/stages/MarketMappingStage";
import { RoleBlueprintStage } from "@components/campaigns/stages/RoleBlueprintStage";
import { ExportButton } from "@components/exports/ExportButton";
import { EXPORT_SELECTION_PROCESS_TO_GOOGLE_DOCS } from "@lib/graphql/mutations";
import { CAMPAIGN_BRIEF_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import type { ArchivedStep, SelectionProcessStep } from "@lib/selectionSteps";
import { useCallback } from "react";
import { useMutation, useQuery } from "urql";
import { useCampaignDetail } from "./context";
import { SlotStageActions } from "./slotStageActions";

// DEFINE slot bodies — Epic 0 #2216 lifts the role/sourcing definition editors
// out of the StageWorkspace stepper into the DEFINE surface. These are thin
// wrappers: each reads the campaign id from the forwarded outlet context and
// renders the existing self-contained stage component unchanged. Stage actions
// (first-run generation etc.) re-home here from the retired stepper (#2219).

export function BriefDefineSlot() {
  const { campaign } = useCampaignDetail();
  // BriefStage → BriefSection bundles brief + personas + screening criteria.
  return (
    <div className="space-y-4">
      <SlotStageActions campaign={campaign} stages={[1]} />
      <BriefStage campaignId={campaign.id} />
    </div>
  );
}

export function BlueprintDefineSlot() {
  const { campaign } = useCampaignDetail();
  return (
    <div className="space-y-4">
      <SlotStageActions campaign={campaign} stages={[2]} />
      <RoleBlueprintStage campaignId={campaign.id} />
    </div>
  );
}

export function MarketMapDefineSlot() {
  const { campaign } = useCampaignDetail();
  return (
    <div className="space-y-4">
      <SlotStageActions campaign={campaign} stages={[3]} />
      <MarketMappingStage campaignId={campaign.id} />
    </div>
  );
}

export function SelectionProcessDefineSlot() {
  const { campaign } = useCampaignDetail();
  const [{ data, fetching }, reexecute] = useQuery({
    query: CAMPAIGN_BRIEF_QUERY,
    variables: { id: campaign.id },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaign.id, refresh);
  const [, exportToGoogleDocs] = useMutation(EXPORT_SELECTION_PROCESS_TO_GOOGLE_DOCS);

  const startExport = async () => {
    const result = await exportToGoogleDocs({ input: { campaignId: campaign.id } });
    const payload = result.data?.exportSelectionProcessToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  const brief = data?.campaign?.brief;

  if (fetching && !brief) {
    return <p className="text-sm text-text-muted">Loading selection process...</p>;
  }

  // The selection process hangs off the brief; there's nothing to define until
  // the brief exists.
  if (!brief) {
    return (
      <div className="rounded-lg bg-card p-6 text-center shadow-sm ring-1 ring-ring">
        <p className="text-sm text-text-muted">Define the brief first</p>
        <p className="mt-1 text-xs text-text-faint">
          The selection process is captured against the campaign brief.
        </p>
      </div>
    );
  }

  type RawParticipant = {
    role: string;
    name: string | null;
    organisationMembershipId: string | null;
  };
  type RawCoverage = {
    dimensionId: string;
    depth: string;
    dimensionName: string | null;
    dimensionInCurrentMatrix: boolean;
  };
  type RawStepAssessmentPlan = {
    intent: string | null;
    coverage: RawCoverage[] | null;
    currentVersion: {
      versionNumber: number;
      origin: string;
      acknowledgedAt: string | null;
    } | null;
  } | null;
  type RawStep = {
    id: string;
    continuityId: string;
    stepType: string;
    label: string | null;
    stepFormat: string | null;
    durationMinutes: number | null;
    participants: RawParticipant[] | null;
    purpose: string | null;
    inFlightCandidateCount: number;
    stepAssessmentPlan: RawStepAssessmentPlan;
  };

  type RawArchivedStep = {
    continuityId: string;
    lastLabel: string | null;
    lastStepType: string | null;
    removedAt: string | null;
    removedBy: string | null;
    changeReason: string | null;
    inFlightCandidateCount: number;
  };

  const rawSteps: RawStep[] = brief.selectionProcess?.steps ?? [];
  const rawArchived: RawArchivedStep[] = brief.selectionProcess?.archivedSteps ?? [];

  const steps: SelectionProcessStep[] = rawSteps.map((s) => {
    const plan = s.stepAssessmentPlan;
    return {
      id: s.id,
      continuityId: s.continuityId,
      stepType: s.stepType,
      label: s.label ?? null,
      stepFormat: s.stepFormat ?? null,
      durationMinutes: s.durationMinutes ?? null,
      participants: (s.participants ?? []).map((p) => ({
        role: p.role,
        name: p.name ?? null,
        organisationMembershipId: p.organisationMembershipId ?? null,
      })),
      purpose: s.purpose ?? null,
      inFlightCandidateCount: s.inFlightCandidateCount,
      assessmentPlan: plan
        ? {
            intent: plan.intent ?? null,
            coverage: (plan.coverage ?? []).map((c) => ({
              dimensionId: c.dimensionId,
              depth: c.depth,
              dimensionName: c.dimensionName ?? null,
              dimensionInCurrentMatrix: c.dimensionInCurrentMatrix,
            })),
            versionNumber: plan.currentVersion?.versionNumber ?? null,
            versionOrigin: plan.currentVersion?.origin ?? null,
            acknowledgedAt: plan.currentVersion?.acknowledgedAt ?? null,
          }
        : undefined,
    };
  });

  const archived: ArchivedStep[] = rawArchived.map((a) => ({
    continuityId: a.continuityId,
    lastLabel: a.lastLabel ?? null,
    lastStepType: a.lastStepType ?? null,
    removedAt: a.removedAt ?? null,
    removedBy: a.removedBy ?? null,
    changeReason: a.changeReason ?? null,
    inFlightCandidateCount: a.inFlightCandidateCount,
  }));

  const dimensions: DimensionOption[] = (data?.campaign?.assessmentMatrix?.dimensions ?? []).map(
    (d: { continuityId: string; name: string }) => ({
      continuityId: d.continuityId,
      name: d.name,
    }),
  );

  const coverage = data?.campaign?.selectionProcessCoverage ?? {
    dimensions: [],
    steps: [],
    cells: [],
  };
  const coverageDimensions: CoverageDimension[] = coverage.dimensions ?? [];
  const coverageSteps: CoverageStep[] = coverage.steps ?? [];
  const coverageCells: CoverageCell[] = coverage.cells ?? [];
  const matrixOutOfSync: boolean = data?.campaign?.assessmentMatrix?.outOfSync ?? false;

  return (
    <div className="space-y-3">
      {steps.length > 0 && (
        <div className="flex justify-end">
          <ExportButton label="Export to Google Docs" onStart={startExport} />
        </div>
      )}
      <SelectionProcessEditor
        campaignId={campaign.id}
        selectionProcessId={brief.selectionProcess?.id ?? null}
        initial={steps}
        processDescription={brief.selectionProcess?.description ?? null}
        processVersionNumber={brief.selectionProcess?.currentVersion?.versionNumber ?? null}
        processVersionOrigin={brief.selectionProcess?.currentVersion?.origin ?? null}
        locked={brief.locked ?? false}
        dimensions={dimensions}
        matrixOutOfSync={matrixOutOfSync}
        coverage={{
          dimensions: coverageDimensions,
          steps: coverageSteps,
          cells: coverageCells,
        }}
        archived={archived}
        onSaved={refresh}
      />
    </div>
  );
}

export function AssessmentDefineSlot() {
  // The assessment matrix is the scoring-matrix template; per-candidate scores
  // live on the RUN side (#2218). Rendered embedded so the standalone-page
  // chrome is dropped inside the DEFINE surface.
  return <AssessmentMatrixPage embedded />;
}
